import React from "react";
import {
  Box,
  Heading,
  Text,
  List,
  ListItem,
  VStack,
  Divider,
} from "@chakra-ui/react";
import Seo from "../../seo/Seo";
import FaqSection from "../FaqSection";
import { medicalWebPageSchema, serviceSchema } from "../../seo/schema";
import { absoluteUrl } from "../../seo/siteConfig";

// Rendered by <FaqSection> and emitted as FAQPage JSON-LD by <Seo>.
// Keep the two in sync by only editing this array.
const FAQS = [
  {
    q: "How should I take care of my Dental Restoration?",
    a: "To care for your dental restoration, maintain good oral hygiene by brushing twice daily and flossing regularly. Avoid hard or sticky foods that could damage your restoration, and wear a nightguard if you grind your teeth. Visit your dentist regularly for checkups to ensure the restoration remains intact.",
  },
  {
    q: "Are there any Advantages of Composite over amalgam Filling?",
    a: "Composite fillings offer several advantages over amalgam fillings. They blend seamlessly with natural teeth, providing a more aesthetic result. Composites bond directly to the tooth, preserving more of the natural structure. They also don't contain mercury, making them a safer option, and are better for smaller cavities. However, they may be more expensive and slightly less durable for larger cavities.",
  },
  {
    q: "Are there any risks associated with dental fillings?",
    a: "While dental fillings are generally safe, there are potential risks, such as allergic reactions to materials (e.g., mercury in amalgam fillings) or tooth sensitivity. It's important to discuss any concerns with your dentist.",
  },
  {
    q: "Why do I feel pain around my Dental Filling?",
    a: "Pain around a dental filling can result from temporary sensitivity to hot, cold, or pressure. It may also be due to an improper fit, decay under the filling, or tooth damage. Additionally, if the filling is close to the nerve, it can cause discomfort. Persistent pain should be evaluated by a dentist.",
  },
];

const PAGE_PATH = "/dental-filling";
const PAGE_URL = absoluteUrl(PAGE_PATH);

const DentalFilling = () => {
  return (
    <Box bg="#fceee5">
      <Seo
        path={PAGE_PATH}
        title="Dental Filling in Jaipur | Painless Cavity Treatment"
        description="Painless, tooth-coloured dental fillings in Vaishali Nagar, Jaipur. Composite cavity treatment by Dr. Aishwarya Sarda, MDS. Book your appointment today."
        type="article"
        faqs={FAQS}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Dental Fillings", path: PAGE_PATH },
        ]}
        schemas={[
          medicalWebPageSchema({
            url: PAGE_URL,
            title: "Dental Filling in Jaipur | Painless Cavity Treatment",
            description: "Painless, tooth-coloured dental fillings in Vaishali Nagar, Jaipur. Composite cavity treatment by Dr. Aishwarya Sarda, MDS. Book your appointment today.",
            procedureName: "Dental Filling",
          }),
          serviceSchema({
            url: PAGE_URL,
            name: "Dental Filling",
            description: "Painless, tooth-coloured dental fillings in Vaishali Nagar, Jaipur. Composite cavity treatment by Dr. Aishwarya Sarda, MDS. Book your appointment today.",
          }),
        ]}
      />
      <Box maxW="4xl" mx="auto" py={8} px={4} marginTop="80px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "5xl" }}
          mb={6}
          textAlign="center"
          fontWeight="light"
        >
          Understanding Dental Fillings
        </Heading>
        <Text fontSize={{ base: "md", md: "lg" }} fontWeight="normal" mb={4}>
          A dental filling restores a tooth damaged by decay. The dentist removes the decayed portion, cleans the cavity and fills it with composite resin, glass ionomer or amalgam so the tooth regains its shape, strength and function. Most fillings are completed in a single visit under local anaesthesia.
        </Text>
        <Text fontSize="sm" color="gray.600" mb={4} fontStyle="italic">
          By Dr. Aishwarya Sarda, MDS — Conservative Dentistry & Endodontics
        </Text>
        <Divider mb={6} />
        <VStack spacing={4} align="stretch">
          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What is a Dental Filling?
            </Heading>
            <Text mt={2} fontWeight="light">
              A dental filling is a treatment used to restore the function,
              integrity, and shape of a tooth that has been damaged by decay,
              wear, or minor fractures. When a tooth develops a cavity or is
              otherwise compromised, the dentist removes the decayed portion and
              "fills" the area with a durable material to prevent further decay
              and restore the tooth's structure.
            </Text>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Types of Dental Fillings
            </Heading>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Amalgam Fillings:</strong> Made of a mix of metals
                  including mercury, silver, tin, and copper. These fillings are
                  durable, long-lasting, and cost-effective but more noticeable
                  due to their silver color.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Composite Resin Fillings:</strong> Tooth-colored
                  fillings made of a resin material that blends with the natural
                  color of your teeth. Ideal for visible areas, though slightly
                  less durable than amalgam fillings.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Ceramic Fillings:</strong> Made from porcelain, these
                  fillings are tooth-colored, durable, and resistant to
                  staining, often used for larger fillings.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Gold Fillings:</strong> Made from a gold alloy, gold
                  fillings are highly durable and long-lasting. However, they
                  are more expensive and stand out due to their metallic color.
                </Text>
              </ListItem>
              <ListItem>
                <Text mt={2} fontWeight="light">
                  <strong>Glass Ionomer Fillings:</strong> Made of acrylic and
                  glass, these fillings are often used for cavities below the
                  gumline or in children's teeth. They release fluoride, which
                  helps prevent further decay.
                </Text>
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              When Does One Need a Dental Filling?
            </Heading>
            <Text mt={2} fontWeight="light">
              You may need a dental filling if you have:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                A cavity or tooth decay that needs to be filled to prevent
                further damage.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                A chipped or broken tooth that requires restoration.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                A worn-down tooth due to grinding or other causes.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              What Should You Expect in the Procedure for a Dental Filling?
            </Heading>
            <Text mt={2} fontWeight="light">
              The procedure for a dental filling typically involves the
              following steps:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                Your dentist will examine the tooth and may use an X-ray to
                determine the extent of the damage.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                A local anesthetic will be administered to numb the area around
                the affected tooth.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                The decayed portion of the tooth is removed using dental tools.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                The filling material is placed in layers and hardened with a
                special light or chemical process.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Finally, the filling is polished to ensure a smooth finish and
                proper bite alignment.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Consequences of Not Getting a Dental Filling
            </Heading>
            <Text mt={2} fontWeight="light">
              If you don’t get a dental filling when it's needed, the decay can
              worsen and spread, leading to:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                Further decay of the tooth, which could lead to infection or
                abscess.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Tooth sensitivity and pain when eating or drinking.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Potential tooth loss in severe cases where the tooth structure
                is weakened or damaged beyond repair.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <Box>
            <Heading as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" mt={4}>
              Post-Treatment Care for Dental Filling
            </Heading>
            <Text mt={2} fontWeight="light">
              After getting a dental filling, it’s important to follow proper
              care instructions to ensure the filling lasts:
            </Text>
            <List spacing={2} pl={4} styleType="disc">
              <ListItem mt={2} fontWeight="light">
                Avoid eating hard or sticky foods for the first 24 hours to
                allow the filling to fully set.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Brush your teeth gently around the filling to avoid damaging it.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                Floss carefully to avoid dislodging the filling or causing
                damage.
              </ListItem>
              <ListItem mt={2} fontWeight="light">
                If you experience pain or discomfort, consult your dentist for
                adjustments.
              </ListItem>
            </List>
          </Box>

          <Divider />

          <FaqSection items={FAQS} />
        </VStack>
      </Box>
    </Box>
  );
};

export default DentalFilling;
